import { useMemo, useState } from "react";
import { ClientsToCare } from "../clients/CareToday";
import dayjs from "dayjs";
import clsx from "clsx";
import { BackBurnerButton } from "../../components/Family";
import { db, eq } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { useRefData, type Area } from "../../lib/refdata";
import { navigate, setQuery } from "../../lib/router";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Loading, Panel, fmtShort, useToast } from "../../components/ui";
import { useLog } from "../../components/QuickLog";
import { openPerson } from "../../components/PersonDrawer";
import { useAddToPipeline } from "../../components/OppSheet";
import { useDebounced } from "../../lib/hooks";
import { Sheet } from "../../components/ui";
import { LEAD_AREAS } from "../../lib/refdata";

type Item = { section: string; sort_section: number; person_id: string | null; full_name: string; area: Area | null; why: string; due: string | null; ref_id: string | null; detail: any };

const SECTIONS: { key: string; title: string; howto: string; short?: string }[] = [
  { key: "inbound", short: "Reached out", title: "Reply to people who reached out", howto: "inbound" },
  { key: "follow_ups", short: "Follow-ups", title: "Follow-ups you promised", howto: "follow_ups" },
  { key: "met_48h", short: "People met (48h)", title: "Message people you met", howto: "met_48h" },
  { key: "special_dates", short: "Special dates", title: "Birthdays and special dates", howto: "special_dates" },
  { key: "warm_10", short: "Today's 10", title: "Warm market: today's 10", howto: "warm_10" },
  { key: "outreach", short: "Outreach", title: "Outreach: conversations that are alive", howto: "outreach" },
  { key: "networking", short: "Networking", title: "Networking: people you met", howto: "networking" },
  { key: "hnw", short: "HNW", title: "HNW prospects", howto: "hnw" },
  { key: "new_connections", short: "New connections", title: "New connections", howto: "new_connections" },
  { key: "travel", short: "Travel clients", title: "Travel clients", howto: "travel" },
  { key: "travel_prospects", short: "Travel buyers", title: "Travel buyers to reach out to", howto: "travel_prospects" },
];

export default function Today() {
  const ref = useRefData();
  const log = useLog();
  const toast = useToast();
  const addToPipeline = useAddToPipeline();
  const { can } = useSession();
  const today = dayjs().format("YYYY-MM-DD");
  const wd = (dayjs().day() + 6) % 7; // Mon=0

  const items = useQuery(`today:items:${today}`, async () => {
    await db.rpc("ensure_warm_picks", {});
    await db.rpc("travel_ensure_picks", {}).catch(() => undefined);
    return db.rpc<Item[]>("leads_today", {});
  });

  const focus = ref.data?.setting("weekly_focus")?.[wd];
  const howTo = ref.data?.setting("how_to") ?? {};
  const block = ref.data?.setting("morning_block") ?? [];

  const grouped = useMemo(() => {
    const g: Record<string, Item[]> = {};
    // pipeline next actions live on the Command Center (Set next step); team on Team
    for (const it of items.data ?? []) if (it.section !== "team" && it.section !== "pipeline") (g[it.section] ??= []).push(it);
    for (const k of Object.keys(g)) g[k].sort((a, b) => (a.due ?? "").localeCompare(b.due ?? ""));
    return g;
  }, [items.data]);
  const total = Object.values(grouped).reduce((n, l) => n + l.length, 0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const SHOW = 5;
  const present = SECTIONS.filter((x) => grouped[x.key]?.length || (x.key === "warm_10" && items.data));
  const [tab, setTab] = useState("");
  const cur = present.find((x) => x.key === tab) ?? present[0];

  async function skip(it: Item) {
    await db.insert("activities", { area: "warm", person_id: it.person_id, activity_type: "Not now / skip" });
    invalidate("today");
    toast(`${it.full_name} will rest for 30 days`);
  }
  // "Done" asks what happened and what's next: saving the log closes the follow-up
  function doneFollowUp(it: Item) {
    if (!it.person_id) return;
    log({ person_id: it.person_id, person_name: it.full_name, area: (it.area ?? "warm") as Area, follow_up_id: it.ref_id! });
  }

  return (
    <>
      <PageHead help="counts" title="Leads today" sub="Who to reach out to and follow up with from your lead lists." />
      <section className="today-head">
        <div>
          <div className="date">{dayjs().format("dddd, MMMM D")}</div>
          <p className="focus">{focus?.focus ?? "Reach out, log it, close the loops."}</p>
          {focus?.who && <div className="who">Who you mainly work with today: {focus.who}</div>}
        </div>
        <div className="who" style={{ alignSelf: "center" }}>
          <button className="btn small" onClick={() => navigate("/command")}>Today's targets (leads, clients, team) are on the Command Center →</button>
        </div>
      </section>

      <div className="today-cols">
        <div style={{ display: "grid", gap: 14 }}>
          <ErrorBox error={items.error} />
          {items.loading && !items.data && <Panel><Loading rows={6} /></Panel>}
          {items.data && total === 0 && (
            <Panel><Empty title="Nothing due right now">Everyone is within their rhythm. A good moment to add people you met or post content.</Empty></Panel>
          )}
          {cur && items.data && total > 0 && (
            <section className="panel cc-today lt-today">
              <div className="cc-today-h"><h2>Reach out today</h2>
                <span className="small muted">{total} {total === 1 ? "person" : "people"} across {present.length} list{present.length === 1 ? "" : "s"}</span></div>
              <div className="cc-tabs" role="tablist">
                {present.map((x) => (
                  <button key={x.key} role="tab" aria-selected={cur.key === x.key} className={clsx(cur.key === x.key && "on")} onClick={() => setTab(x.key)}>
                    {x.short ?? x.title} ({grouped[x.key]?.length ?? 0})</button>))}
              </div>
              <p className="cc-tabhint small muted">{cur.title}{howTo[cur.howto] ? `: ${howTo[cur.howto]}` : ""}</p>
              {(() => { const s = cur; return s.key === "warm_10" ? (
                <WarmTen bare key={s.key} title={s.title} howto={howTo[s.howto]} items={grouped[s.key] ?? []} onSkip={skip}
              onLog={(it) => log({ person_id: it.person_id!, person_name: it.full_name, area: "warm" })}
              onPipeline={can("pipelines", "edit") ? (it) => addToPipeline({ person_id: it.person_id!, full_name: it.full_name, source_area: "warm" }) : undefined} />
              ) : <>
                <ul className="task-list">
                  {(expanded[s.key] ? grouped[s.key] : grouped[s.key].slice(0, SHOW)).map((it) => {
                  const reached = it.detail?.reached;
                  return (
                    <li key={s.key + (it.ref_id ?? "") + it.person_id} className={clsx("task", reached && "done")}>
                      <button className="name" onClick={() => it.person_id && openPerson(it.person_id)}>{it.full_name}</button>
                      <div className="task-actions">
                        {!reached && it.person_id && (
                          <button className="btn small" onClick={() => log({
                            person_id: it.person_id!, person_name: it.full_name, area: (it.area ?? "warm") as Area,
                            follow_up_id: s.key === "follow_ups" ? it.ref_id! : undefined,
                            activity_type: s.key === "special_dates" ? "Birthday or special-day wish" : undefined,
                          })}>Log</button>
                        )}
                        {it.person_id && s.key !== "pipeline" && it.area && LEAD_AREAS.includes(it.area) && can("pipelines", "edit") && (
                          <button className="btn small ghost" onClick={() => addToPipeline({ person_id: it.person_id!, full_name: it.full_name, source_area: it.area! })}>+ Pipeline</button>
                        )}
                        {s.key === "travel_prospects" && it.person_id && can("activities", "edit") && <button className="btn small ghost" title="Not now: they leave this list and aren't picked again for 4 weeks"
                          onClick={async () => { await db.insert("activities", { area: "travel", person_id: it.person_id, activity_type: "Not now / skip" }); invalidate("today"); toast(`${it.full_name}: skipped for now`); }}>Skip</button>}
                        {s.key === "follow_ups" && <button className="btn small ghost" title="Log what happened and the next step; that closes it" onClick={() => doneFollowUp(it)}>Done…</button>}
                        {!reached && it.person_id && can("activities", "edit") && <BackBurnerButton small person={{ id: it.person_id, full_name: it.full_name }} />}
                        {reached && <span className="pill warm">Reached</span>}
                      </div>
                      <div className="why">
                        {it.why}
                        {it.due && s.key === "follow_ups" && it.detail?.overdue && <span className="pill alert" style={{ marginLeft: 6 }}>since {fmtShort(it.due)}</span>}
                      </div>
                      {s.key === "travel_prospects" && <div className="ctx">{[it.detail?.outcome, it.detail?.phone ?? it.detail?.email, it.detail?.mine && "added by you"].filter(Boolean).join(" · ")}
                        {" · "}<button className="linkish" onClick={() => it.person_id && setQuery({ person: it.person_id, tab: "travel" }, { replace: false })}>trips & quotes</button></div>}
                      {(it.detail?.ask_about || it.detail?.family) && (
                        <div className="ctx">{[it.detail.ask_about && `Ask about: ${it.detail.ask_about}`, it.detail.family && `Family: ${it.detail.family}`].filter(Boolean).join(" · ")}</div>
                      )}
                    </li>
                  );
                  })}
                </ul>
                {grouped[s.key].length > SHOW && (
                  <div className="panel-body cc-more">
                    <button className="linkish" onClick={() => setExpanded((e) => ({ ...e, [s.key]: !e[s.key] }))}>
                      {expanded[s.key] ? `Show only the top ${SHOW}` : `View all ${grouped[s.key].length} →`}
                    </button>
                  </div>
                )}
              </>; })()}
            </section>
          )}
        </div>
        <aside style={{ display: "grid", gap: 14 }}>
          <ClientsToCare />
          <Panel title="Your 6–9 AM block">
            <ul className="task-list">
              {block.map((b: any) => (
                <li key={b.time} className="task"><span><strong className="small">{b.time}</strong><div className="why">{b.what}</div></span></li>
              ))}
            </ul>
          </Panel>
          <DailyNote day={today} />
        </aside>
      </div>
    </>
  );
}

function DailyNote({ day }: { day: string }) {
  const { session } = useSession();
  const toast = useToast();
  const note = useQuery(`today:note:${day}`, () => db.one<{ note: string | null; done: string | null }>("daily_notes", { day: eq(day), user_id: eq(session!.user.id), select: "note,done" }));
  async function save(patch: { note?: string; done?: string }) {
    await db.upsert("daily_notes", { user_id: session!.user.id, day, note: note.data?.note ?? null, done: note.data?.done ?? null, ...patch }, "user_id,day");
    invalidate(`today:note:${day}`);
    toast("Saved");
  }
  return (
    <Panel title="Close the day" hint="One line on how it went">
      <div className="panel-body" style={{ display: "grid", gap: 8 }}>
        <textarea className="textarea" defaultValue={note.data?.note ?? ""} key={note.data?.note ?? "empty"}
          onBlur={(e) => e.target.value !== (note.data?.note ?? "") && save({ note: e.target.value })} placeholder="How did today go?" />
        <div className="seg">
          {["Yes", "No", "N/A"].map((v) => (
            <button key={v} aria-pressed={note.data?.done === v} onClick={() => save({ done: v })}>6–9 block {v === "Yes" ? "done" : v === "No" ? "missed" : "n/a"}</button>
          ))}
        </div>
      </div>
    </Panel>
  );
}

/** Today's 10: the system's 5 (a fair random draw) and your 5 (chosen here). */
function WarmTen({ title, howto, items, onLog, onSkip, onPipeline, bare }: {
  bare?: boolean; title: string; howto?: string; items: Item[]; onLog: (it: Item) => void; onSkip: (it: Item) => void; onPipeline?: (it: Item) => void;
}) {
  const [picking, setPicking] = useState(false);
  const random = items.filter((i) => i.detail?.kind === "auto");
  const mine = items.filter((i) => i.detail?.kind === "mine");
  const left = Math.max(0, 5 - mine.length);
  const toast = useToast();
  async function toOutreach(it: Item) {
    if (!it.ref_id) return;
    await db.update("person_areas", { id: eq(it.ref_id) }, { outreach_since: dayjs().format("YYYY-MM-DD") });
    invalidate("today"); invalidate("people"); toast(`${it.full_name} moved to Outreach`);
  }
  async function unpick(it: Item) { await db.rpc("unpick_warm", { p_person: it.person_id }); invalidate("today"); toast(`${it.full_name} removed from your picks`); }
  const row = (it: Item, isMine: boolean) => {
    const reached = it.detail?.reached;
    return (
      <li key={it.person_id} className={clsx("task", reached && "done")}>
        <button className="name" onClick={() => it.person_id && openPerson(it.person_id)}>{it.full_name}</button>
        <div className="task-actions">
          {!reached && <button className="btn small" onClick={() => onLog(it)}>Log</button>}
          {onPipeline && <button className="btn small ghost" onClick={() => onPipeline(it)}>+ Pipeline</button>}
          {!reached && !isMine && <button className="btn small ghost" onClick={() => onSkip(it)}>Not now</button>}
          {!reached && it.person_id && <BackBurnerButton small person={{ id: it.person_id, full_name: it.full_name }} />}
          <button className="btn small ghost" onClick={() => toOutreach(it)} title="The connection is reignited: take them out of the warm market">→ Outreach</button>
          {!reached && isMine && <button className="btn small ghost" onClick={() => unpick(it)}>Remove</button>}
          {reached && <span className="pill warm">Reached</span>}
        </div>
        <div className="why">{it.why}</div>
        {(it.detail?.ask_about || it.detail?.family) && (
          <div className="ctx">{[it.detail.ask_about && `Ask about: ${it.detail.ask_about}`, it.detail.family && `Family: ${it.detail.family}`].filter(Boolean).join(" · ")}</div>
        )}
      </li>
    );
  };
  const done = items.filter((i) => i.detail?.reached).length;
  const body = <>
      {!bare && howto && <p className="section-howto">{howto}</p>}
      {bare && <p className="small muted" style={{ margin: "8px 20px 0" }}>{done} of {items.length} reached today</p>}
      <div className="w10">
        <div>
          <h3 className="w10-h"><span className="w10-dot rnd" />Picked at random <span className="muted">· {random.length}</span></h3>
          <p className="w10-sub">A fair draw from everyone eligible, so no one is left out by habit.</p>
          <ul className="task-list">{random.map((it) => row(it, false))}</ul>
          {random.length === 0 && <p className="w10-sub" style={{ padding: "0 20px 12px" }}>No one eligible today: everyone is resting, in outreach or in a pipeline.</p>}
        </div>
        <div>
          <h3 className="w10-h"><span className="w10-dot mine" />Your 5 <span className="muted">· {mine.length} of 5</span>
            {left > 0 && <button className="btn small primary" style={{ marginLeft: "auto" }} onClick={() => setPicking(true)}>Pick {left} more</button>}
          </h3>
          <p className="w10-sub">People you choose for today. You can change them until you reach out.</p>
          <ul className="task-list">{mine.map((it) => row(it, true))}</ul>
        </div>
      </div>
      {picking && <WarmPicker left={left} onClose={() => setPicking(false)} />}
    </>;
  return bare ? body : <Panel title={title} hint={`${done} of ${items.length} reached today`}>{body}</Panel>;
}

function WarmPicker({ left, onClose }: { left: number; onClose: () => void }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [err, setErr] = useState<Error | null>(null);
  const [added, setAdded] = useState(0);
  const toast = useToast();
  const list = useQuery(`today:candidates:${dq}:${added}`, () => db.rpc<any[]>("warm_pick_candidates", { p_search: dq || null, p_limit: 60 }));
  const remaining = left - added;
  async function pick(p: any) {
    setErr(null);
    try { await db.rpc("pick_warm", { p_person: p.person_id }); setAdded((n) => n + 1); invalidate("today:items"); toast(`${p.full_name} added to your 5`); }
    catch (e) { setErr(e as Error); }
  }
  return (
    <Sheet title={remaining > 0 ? `Pick ${remaining} for today` : "You have your 5"} onClose={onClose}
      footer={<button className="btn primary" onClick={onClose}>Done</button>}>
      <ErrorBox error={err} />
      <div className="search"><input className="input" autoFocus placeholder="Search name, bond or what to ask about" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      <p className="small muted" style={{ margin: 0 }}>Longest since you last spoke first. People resting, in outreach, in a pipeline or picked this week aren't shown.</p>
      {!list.data ? <Loading rows={5} /> : list.data.length === 0 ? <Empty title="No one matches" /> : (
        <ul className="task-list pick-list">
          {list.data.map((p) => (
            <li key={p.person_id} className="task">
              <span className="name" style={{ cursor: "default" }}>{p.full_name}</span>
              <div className="task-actions"><button className="btn small" disabled={remaining <= 0} onClick={() => pick(p)}>Pick</button></div>
              <div className="why">{[p.bond, p.last_touch ? `last spoke ${dayjs().diff(dayjs(p.last_touch), "day")} days ago` : "never reached", p.days_to_birthday != null && p.days_to_birthday <= 14 ? `birthday in ${p.days_to_birthday}d` : null].filter(Boolean).join(" · ")}</div>
              {p.ask_about && <div className="ctx">Ask about: {p.ask_about}</div>}
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}
